import { getActiveExperimentProps } from "./experiments/active-props";
import { isLocale } from "./i18n/locales";

/**
 * The props EVERY client-side analytics event carries, whatever fires it.
 *
 * One function so a new cross-cutting dimension is added once rather than at
 * the four places that need it — `trackEvent`, `trackUnloadEvent`, Plausible's
 * `customProperties` and PostHog's `before_send`. Two of those are SDK hooks
 * called per event, which is what makes auto-captured pageviews carry the same
 * dimensions as our own goal events; a funnel segmented on one of these has no
 * holes on the steps we did not instrument by hand.
 */

/**
 * The locale the page is actually RENDERED in, read off `<html lang>`.
 *
 * That attribute is the resolved locale by definition (the root layout sets it
 * from the same value it hands the Lingui provider), so it needs no plumbing
 * and is correct for both halves of the locale rule at once: the URL on
 * marketing pages, the preference everywhere else.
 *
 * Deliberately NOT the `sq_lang` cookie. That is the *preference*, which is
 * the wrong answer on a marketing page — /terms renders English for a reader
 * whose preference is German, and an event tagged `de` there would say the
 * German site converted someone it never showed.
 *
 * It is also race-free in a way the experiment props are not: the attribute
 * ships in the server-rendered HTML, so it is readable before any script runs.
 */
const renderedLocale = (): Record<string, string> => {
  if (typeof document === "undefined") return {};
  const lang = document.documentElement.lang;
  return isLocale(lang) ? { locale: lang } : {};
};

/**
 * Experiment assignments plus the rendered locale.
 *
 * NOTE FOR WHOEVER ADDS THE NEXT ONE: a property is ingested immediately but
 * only becomes queryable in Plausible once it is registered under Site
 * Settings → Properties. That is a dashboard step, not a deploy
 * (apps/web/CLAUDE.md).
 */
export const getBaseAnalyticsProps = (): Record<string, string> => ({
  ...getActiveExperimentProps(),
  ...renderedLocale(),
});
